import { create } from 'zustand'
import { persist, createJSONStorage } from 'zustand/middleware'
import { track } from '@/shared/lib'
import type {
  ContentPack,
  RawContentPack,
  Species,
  DndClass,
  Background,
  Spell,
  EquipmentItem,
  Feat,
  PackAuthor,
} from '@/entities/content/types/content'
import { attachSubclasses, resolveEntities, buildProvenance, withOptionalLineages } from '../lib/contentPacks'
import { unresolvedListRefs, unresolvedTraitDrops, validateRawPack } from '../lib/validatePack'
import { fetchPack } from '../lib/syncPack'
import {
  applyFetch,
  isSyncDue,
  newSubscription,
  reconcilePartySubscriptions,
  subscriptionPacks,
} from '../lib/subscriptionState'
import type {
  PackPointer,
  RemotePackSubscription,
  SubscriptionOptions,
  SubscriptionScope,
} from '../types/subscription'
import { SRD_PACK_ID } from '@/shared/config'
import srdSpecies from '../data/srd/species.json'
import { SRD_CLASSES } from '../data/srd/classes'
import srdBackgrounds from '../data/srd/backgrounds.json'
import srdSpells from '../data/srd/spells.json'
import srdEquipment from '../data/srd/equipment.json'
import srdFeats from '../data/srd/feats.json'

export { SRD_PACK_ID } from '@/shared/config/srd'

/**
 * How stale a subscription's last check must be before an on-load auto-sync
 * re-fetches it. Keeps reopening the app from re-hitting every source each time;
 * the manual "Check for updates" button bypasses this.
 */
const SYNC_COOLDOWN_MS = 15 * 60 * 1000

const SRD_PACK: ContentPack = {
  id: SRD_PACK_ID,
  name: 'SRD 5.2.1',
  version: '5.2.1',
  species: srdSpecies as Species[],
  classes: SRD_CLASSES,
  backgrounds: srdBackgrounds as Background[],
  spells: srdSpells as Spell[],
  equipment: srdEquipment as EquipmentItem[],
  feats: srdFeats as Feat[],
}

/** The six content arrays on a pack: the keys the manage UI can list and delete from. */
export type ContentArrayKey = 'species' | 'classes' | 'backgrounds' | 'spells' | 'equipment' | 'feats'
/** Every entry array a pack file can hold: the content types plus `subclasses` for existing classes. */
export type PackArrayKey = ContentArrayKey | 'subclasses'

const CONTENT_ARRAY_KEYS: ContentArrayKey[] = [
  'species',
  'classes',
  'backgrounds',
  'spells',
  'equipment',
  'feats',
]

/** Total number of entries across a pack's arrays, subclasses included (a pack may hold only those). */
export function packEntryCount(pack: RawContentPack): number {
  return [...CONTENT_ARRAY_KEYS, 'subclasses' as const].reduce((sum, key) => sum + (pack[key]?.length ?? 0), 0)
}

/** An import held back because its name clashes with an existing pack, awaiting the user's choice. */
export interface PendingImport {
  /** The parsed pack waiting to be committed. */
  pack: RawContentPack
  /** Id of the already-imported pack it would overwrite. */
  existingId: string
  /** The already-imported pack's display name, for the confirmation prompt. */
  name: string
}

interface ContentStore {
  packs: RawContentPack[]
  importError: string | null
  /**
   * Imports whose names match an existing pack, queued in upload order; the
   * first is shown in the confirmation dialog and resolved by confirm/cancel.
   */
  pendingImports: PendingImport[]

  species: Species[]
  classes: DndClass[]
  backgrounds: Background[]
  spells: Spell[]
  equipment: EquipmentItem[]
  feats: Feat[]

  /** Per content type: resolved entity id → the id of the pack it came from. */
  provenance: Record<PackArrayKey, Record<string, string>>
  /** Pack id → display name, for every loaded pack (including SRD). */
  packNames: Record<string, string>
  /** Pack id → its declared author, for the packs that have one. */
  packAuthors: Record<string, PackAuthor>

  /** Remote pack subscriptions (read-only content pulled from a publisher's URL). */
  subscriptions: RemotePackSubscription[]

  /** Import one or more pack files; per-file errors accumulate into `importError`. */
  importContentPacks: (files: File[]) => Promise<void>
  /**
   * Subscribe to a remote pack by pointer and kick off the first sync. Re-using an
   * existing subscription's URL just refreshes it. Returns the subscription id.
   */
  subscribe: (pointer: PackPointer) => Promise<string>
  /** Drop a subscription and re-merge (its content disappears from the sheet). */
  unsubscribe: (id: string) => void
  /** Fetch a subscription and fold the result in. `force` ignores the paused flag. */
  syncSubscription: (id: string, opts?: { force?: boolean }) => Promise<void>
  /** Apply an `update-available` revision (re-fetch and commit, ignoring autoUpdate). */
  applyUpdate: (id: string) => Promise<void>
  /** Update a subscription's options (autoUpdate / paused). */
  setSubscriptionOptions: (id: string, options: Partial<SubscriptionOptions>) => void
  /** Sync every non-paused subscription whose last check is older than the cooldown. */
  syncDueSubscriptions: (cooldownMs?: number) => void
  /**
   * Reconcile party-inherited pointers (from the party store) into the
   * subscription set: adds/fetches new ones, evicts those no longer shared, and
   * leaves personal subscriptions untouched. Party packs merge before personal.
   */
  setPartyPointers: (desired: { pointer: PackPointer; scope: SubscriptionScope }[]) => void
  /** Commit the first pending import, overwriting the clashing pack. */
  confirmImport: () => void
  /** Discard the first pending import, leaving existing content untouched. */
  cancelImport: () => void
  removeEntry: (packId: string, type: PackArrayKey, index: number) => void
  removePack: (packId: string) => void
  clearImportError: () => void
}

/**
 * Flatten every pack's entries per content type and resolve them (full entities
 * + `base` extensions) into concrete arrays. Bases resolve against the combined
 * set across all packs, so a pack can extend SRD ids. Resolution problems are
 * collected into `errors`.
 */
function mergeContent(packs: RawContentPack[], errors: string[]) {
  const classes = attachSubclasses(
    resolveEntities<DndClass>(packs.flatMap((p) => p.classes ?? []), errors),
    packs.flatMap((p) => p.subclasses ?? []),
    errors,
  )
  const species = withOptionalLineages(
    resolveEntities<Species>(packs.flatMap((p) => p.species ?? []), errors),
    packs.flatMap((p) => p.species ?? []),
  )
  // A lineage's dropped trait names resolve only once its species' base is merged in.
  errors.push(...unresolvedTraitDrops(species))
  const spells = resolveEntities<Spell>(packs.flatMap((p) => p.spells ?? []), errors)
  const feats = resolveEntities<Feat>(packs.flatMap((p) => p.feats ?? []), errors)
  // spellList/featList names can only be resolved once every pack is merged,
  // since one pack may name an entry another provides.
  errors.push(...unresolvedListRefs(classes, spells, feats))
  return {
    species,
    classes,
    backgrounds: resolveEntities<Background>(packs.flatMap((p) => p.backgrounds ?? []), errors),
    spells,
    equipment: resolveEntities<EquipmentItem>(packs.flatMap((p) => p.equipment ?? []), errors),
    feats,
    provenance: buildProvenance(packs),
    packNames: Object.fromEntries(packs.map((p) => [p.id, p.name])),
    packAuthors: Object.fromEntries(packs.flatMap((p) => (p.author ? [[p.id, p.author]] : []))),
  }
}

function validateContentPack(data: unknown): data is RawContentPack {
  if (!data || typeof data !== 'object') return false
  const d = data as Record<string, unknown>
  if (typeof d.id !== 'string' || typeof d.name !== 'string') return false
  // Each content array is optional, but must be an array when present.
  for (const key of ['species', 'classes', 'subclasses', 'backgrounds', 'spells', 'equipment', 'feats']) {
    if (d[key] !== undefined && !Array.isArray(d[key])) return false
  }
  return true
}

/**
 * Every RawContentPack that feeds the resolver: local/imported packs first, then
 * subscription caches. A synced subscription is just another content source, so
 * `mergeContent` is unchanged — it only sees a longer list.
 */
/** Every raw pack merged into content: imported ones, then subscriptions' cached copies. */
export function allPacks(packs: RawContentPack[], subscriptions: RemotePackSubscription[]): RawContentPack[] {
  return [...packs, ...subscriptionPacks(subscriptions)]
}

/** Lenient shape check for a persisted subscription (its cache was validated when fetched). */
function isStoredSubscription(x: unknown): x is RemotePackSubscription {
  if (!x || typeof x !== 'object') return false
  const s = x as Record<string, unknown>
  const pointer = s.pointer as Record<string, unknown> | undefined
  return (
    typeof s.subscriptionId === 'string' &&
    !!pointer &&
    typeof pointer.url === 'string' &&
    typeof s.status === 'object' &&
    typeof s.options === 'object'
  )
}

function initialMerge(packs: RawContentPack[]) {
  return mergeContent(packs, [])
}

/**
 * Re-merge after a pack set change and produce the state patch. On a resolution
 * error the new packs are rejected (existing content stays put) and only the error
 * surfaces, so content never lands half-resolved. Subscriptions are folded in so an
 * import never drops subscribed content from the derived arrays.
 */
function commitPacks(
  packs: RawContentPack[],
  subscriptions: RemotePackSubscription[],
): Partial<ContentStore> {
  const errors: string[] = []
  const merged = mergeContent(allPacks(packs, subscriptions), errors)
  if (errors.length) return { importError: errors.join(' ') }
  return { packs, importError: null, ...merged }
}

/** Append `msg` to any error already collected during the current import batch. */
function appendError(prior: string | null, msg: string | null | undefined): string | null {
  if (!msg) return prior
  return prior ? `${prior} ${msg}` : msg
}

export const useContentStore = create<ContentStore>()(
  persist(
    (set, get) => ({
  packs: [SRD_PACK],
  importError: null,
  pendingImports: [],
  subscriptions: [],
  ...initialMerge([SRD_PACK]),

  importContentPacks: async (files: File[]) => {
    // Fresh batch: drop errors left over from a previous import, then process the
    // files one by one so each failure is reported (prefixed with its file name)
    // without aborting the rest.
    set({ importError: null })
    for (const file of files) {
      const fail = (msg: string) =>
        set((state) => ({ importError: appendError(state.importError, `${file.name}: ${msg}`) }))
      try {
        const text = await file.text()
        const data: unknown = JSON.parse(text)
        if (!validateContentPack(data)) {
          fail('Invalid content pack format. Please check the schema.')
          continue
        }
        // Same schema check a SUBSCRIBED pack goes through (see syncPack.ts):
        // a file dropped in here is no more trusted than one fetched from a URL,
        // so both entry points must refuse the same malformed content.
        const problems = validateRawPack(data)
        if (problems.length) {
          fail(problems.join(' '))
          continue
        }
        // A clash with an already-imported pack, by name or by id (a new version of it that
        // was renamed), is the user's call: queue the import and let them overwrite or
        // cancel. Never keep two packs with one id. (SRD ships in code and can't clash.)
        const existing = get().packs.find((p) => p.id !== SRD_PACK.id && (p.id === data.id || p.name === data.name))
        if (existing) {
          set((state) => ({
            pendingImports: [
              ...state.pendingImports,
              { pack: data, existingId: existing.id, name: existing.name },
            ],
          }))
          continue
        }
        set((state) => {
          const patch = commitPacks([...state.packs, data], state.subscriptions)
          return { ...patch, importError: appendError(state.importError, patch.importError) }
        })
        track('content-pack-imported')
      } catch {
        fail('Failed to read file. Make sure it is a valid JSON file.')
      }
    }
  },

  confirmImport: () =>
    set((state) => {
      const [pending, ...rest] = state.pendingImports
      if (!pending) return {}
      // Drop the clashing pack and append the new one in its place.
      const packs = [...state.packs.filter((p) => p.id !== pending.existingId), pending.pack]
      track('content-pack-imported')
      return { ...commitPacks(packs, state.subscriptions), pendingImports: rest }
    }),

  cancelImport: () => set((state) => ({ pendingImports: state.pendingImports.slice(1) })),

  // Delete a single imported entry, then re-merge. We commit the re-merge even if
  // it produces errors: e.g. removing a `base` that another pack's extension relies
  // on just drops that dependent from the resolved arrays (its raw entry stays, so
  // restoring the base brings it back). The SRD pack is never editable.
  removeEntry: (packId, type, index) =>
    set((state) => {
      if (packId === SRD_PACK.id) return {}
      const packs = state.packs
        .map((p): RawContentPack => {
          if (p.id !== packId) return p
          const arr = p[type] ?? []
          return { ...p, [type]: arr.filter((_, i) => i !== index) }
        })
        // Removing the last entry empties the pack; drop it rather than keep a husk.
        .filter((p) => p.id === SRD_PACK.id || packEntryCount(p) > 0)
      return { packs, importError: null, ...mergeContent(allPacks(packs, state.subscriptions), []) }
    }),

  // Remove an entire imported pack (never the SRD pack) and re-merge.
  removePack: (packId) =>
    set((state) => {
      if (packId === SRD_PACK.id) return {}
      const packs = state.packs.filter((p) => p.id !== packId)
      return { packs, importError: null, ...mergeContent(allPacks(packs, state.subscriptions), []) }
    }),

  subscribe: async (pointer) => {
    // Re-subscribing to the same URL refreshes the existing one instead of duplicating.
    const existing = get().subscriptions.find((s) => s.pointer.url === pointer.url)
    if (existing) {
      await get().syncSubscription(existing.subscriptionId, { force: true })
      return existing.subscriptionId
    }
    const sub = newSubscription(pointer)
    set((state) => ({ subscriptions: [...state.subscriptions, sub] }))
    await get().syncSubscription(sub.subscriptionId, { force: true })
    return sub.subscriptionId
  },

  syncSubscription: async (id, opts) => {
    const sub = get().subscriptions.find((s) => s.subscriptionId === id)
    if (!sub || (sub.options.paused && !opts?.force)) return
    // Mark in-flight so the UI can show a spinner; the result patch overwrites it.
    set((state) => ({
      subscriptions: state.subscriptions.map((s) =>
        s.subscriptionId === id ? { ...s, status: { ...s.status, state: 'checking' } } : s,
      ),
    }))
    const result = await fetchPack(sub.pointer)
    set((state) => {
      const subscriptions = state.subscriptions.map((s) =>
        s.subscriptionId === id ? applyFetch(s, result) : s,
      )
      return { subscriptions, ...mergeContent(allPacks(state.packs, subscriptions), []) }
    })
  },

  applyUpdate: async (id) => {
    const sub = get().subscriptions.find((s) => s.subscriptionId === id)
    if (!sub) return
    const result = await fetchPack(sub.pointer)
    set((state) => {
      const subscriptions = state.subscriptions.map((s) =>
        s.subscriptionId === id ? applyFetch(s, result, true) : s,
      )
      return { subscriptions, ...mergeContent(allPacks(state.packs, subscriptions), []) }
    })
  },

  unsubscribe: (id) =>
    set((state) => {
      const subscriptions = state.subscriptions.filter((s) => s.subscriptionId !== id)
      return { subscriptions, ...mergeContent(allPacks(state.packs, subscriptions), []) }
    }),

  setSubscriptionOptions: (id, options) =>
    set((state) => ({
      subscriptions: state.subscriptions.map((s) =>
        s.subscriptionId === id ? { ...s, options: { ...s.options, ...options } } : s,
      ),
    })),

  syncDueSubscriptions: (cooldownMs = SYNC_COOLDOWN_MS) => {
    const now = Date.now()
    for (const sub of get().subscriptions) {
      if (isSyncDue(sub, now, cooldownMs)) void get().syncSubscription(sub.subscriptionId)
    }
  },

  setPartyPointers: (desired) => {
    const { next, added } = reconcilePartySubscriptions(get().subscriptions, desired)
    set((state) => ({ subscriptions: next, ...mergeContent(allPacks(state.packs, next), []) }))
    for (const id of added) void get().syncSubscription(id, { force: true })
  },

  clearImportError: () => set({ importError: null }),
    }),
    {
      name: 'dnd-content-packs',
      version: 1,
      storage: createJSONStorage(() => localStorage),
      // Persist user-imported packs and subscriptions (pointer + cached last-known-good
      // content, which is the user's own offline copy). SRD ships in code; derived
      // arrays recompute on load.
      partialize: (state) => ({
        packs: state.packs.filter((p) => p.id !== SRD_PACK.id),
        subscriptions: state.subscriptions,
      }),
      // On hydration: prepend SRD, restore subscriptions, re-resolve all extensions
      // across both. A pack (or subscription cache) extending a missing base drops out.
      merge: (persisted, current) => {
        const data = persisted as
          | { packs?: RawContentPack[]; subscriptions?: RemotePackSubscription[] }
          | undefined
        const packs: RawContentPack[] = [SRD_PACK, ...(data?.packs ?? []).filter(validateContentPack)]
        const subscriptions = (data?.subscriptions ?? []).filter(isStoredSubscription)
        return { ...current, packs, subscriptions, ...mergeContent(allPacks(packs, subscriptions), []) }
      },
    },
  ),
)

/**
 * Returns a resolver `(type, id) => packName | null`. The name is non-null only
 * when the entity comes from an imported (non-SRD) pack, i.e. exactly the
 * options that should carry a content-pack flag.
 */
export function usePackSource() {
  const provenance = useContentStore((s) => s.provenance)
  const packNames = useContentStore((s) => s.packNames)
  return (type: PackArrayKey, id: string): string | null => {
    const packId = provenance[type]?.[id]
    if (!packId || packId === SRD_PACK_ID) return null
    return packNames[packId] ?? packId
  }
}
